import {
    trimAddressInput,
    toAddressInputKey,
    createAddressInput,
    updateAddressInput,
    type AddressInputSource,
    type AddressingCountryWithoutName,
    type AddressField,
    type AddressFieldWithCountry,
    type ExtendedAddressInputKey,
    type AddressInput,
    type AddressingCountryCode,
} from '@precon-tools-suite-by-shared/js-commerceguys-addressing';
import { useAddressingCountryWithoutName } from './countries';
import { extendRef } from '@vueuse/core';
import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

export type UseAddressInputOptions = {
    watchSource?: boolean;
    onChanged?: (newValue: AddressInput) => void;
    defaultCountryCode?: string;
};

export function useAddressInput(
    source?: MaybeRefOrGetter<AddressInputSource>,
    { watchSource, defaultCountryCode, onChanged }: UseAddressInputOptions = {}
) {
    const storedState = ref<AddressInput>(
        createAddressInput(toValue(source), defaultCountryCode)
    );

    const _country = useAddressingCountryWithoutName(
        () => storedState.value.countryCode
    );

    function update(source: AddressInputSource) {
        const changed = updateAddressInput(storedState.value, source);
        if (changed) {
            onChanged?.(
                trimAddressInput(
                    storedState.value,
                    _country.value?.addressFormat?.usedFields ?? []
                )
            );
        }
        return changed;
    }

    if (watchSource) {
        watch(() => toValue(source), update);
    }

    const addressInput = computed<AddressInput>({
        get: () =>
            trimAddressInput(
                storedState.value,
                _country.value?.addressFormat?.usedFields ?? []
            ),
        set: (newValue) => {
            update(newValue);
        },
    });

    const country = computed<AddressingCountryWithoutName | undefined>({
        get: () => _country.value,
        set: (newValue) => {
            update({ country: newValue });
        },
    });

    const fields = {
        countryCode: computed<AddressingCountryCode>({
            get: () => addressInput.value.countryCode,
            set: (countryCode) => update({ countryCode }),
        }),
        addressLine1: computed<string | null>({
            get: () => addressInput.value.addressLine1,
            set: (addressLine1) => update({ addressLine1 }),
        }),
        addressLine2: computed<string | null>({
            get: () => addressInput.value.addressLine2,
            set: (addressLine2) => update({ addressLine2 }),
        }),
        administrativeArea: computed<string | null>({
            get: () => addressInput.value.administrativeArea,
            set: (administrativeArea) => update({ administrativeArea }),
        }),
        dependentLocality: computed<string | null>({
            get: () => addressInput.value.dependentLocality,
            set: (dependentLocality) => update({ dependentLocality }),
        }),
        locality: computed<string | null>({
            get: () => addressInput.value.locality,
            set: (locality) => update({ locality }),
        }),
        postalCode: computed<string | null>({
            get: () => addressInput.value.postalCode,
            set: (postalCode) => update({ postalCode }),
        }),
        sortingCode: computed<string | null>({
            get: () => addressInput.value.sortingCode,
            set: (sortingCode) => update({ sortingCode }),
        }),
    };

    function getField(field: 'COUNTRY' | 'countryCode'): AddressingCountryCode;
    function getField(
        field: AddressFieldWithCountry | ExtendedAddressInputKey
    ): string | null;
    function getField(
        field: AddressFieldWithCountry | ExtendedAddressInputKey
    ): string | null {
        const key = toAddressInputKey(field);
        if (!key) return null;
        return addressInput.value[key] ?? null;
    }

    function setField(
        field: AddressField | ExtendedAddressInputKey,
        value: string | null
    ): boolean {
        const key = toAddressInputKey(field);
        if (!key) return false;
        return update({ [key]: value });
    }

    return extendRef(
        addressInput,
        {
            update,
            getField,
            setField,
            country,
            ...fields,
        },
        {
            unwrap: true,
        }
    );
}
